import { parseChangeRequestSummary } from "@sd/lib/changeRequestTicketFields";
import { pickProjectNameFromApi } from "@sd/lib/ticketRowMapping";
import type { Ticket } from "@sd/types/ticket";
import {
  parseIsExitFromApi,
  parseIsPtsFromApi,
  parseIsVipFromApi,
  parseIsSlaFromApi,
} from "@sd/lib/ticketApiBooleans";
import { buildAttachmentsFromTicketRecord } from "@sd/lib/parseAttachmentIds";
import { ticketPriorityFieldsFromApi } from "@sd/lib/ticketPriorityFields";
import {
  enrichStreamRowsWithPriorityMap,
  enrichStreamRowsWithUserMap,
  rowFromUnknown,
  streamRowToTicket,
} from "@sd/lib/ticketStreamUtils";

function readIdLike(value: unknown): string | undefined {
  if (typeof value === "string" || typeof value === "number") {
    const id = String(value).trim();
    return id || undefined;
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return readIdLike(record.id ?? record.uuid ?? record.value ?? record.project_id);
  }
  return undefined;
}

/** Map GET `/service_desk/ticket_create/{id}/` payload → agent workbench `Ticket`. */
export function workbenchTicketFromDetailPayload(
  raw: Record<string, unknown>,
  priorityIdToLabel: Map<string, string>,
  userDisplayMap: Map<string, string>,
  agents: { id: string; name: string }[],
  slaPriorityById?: Record<string, string>
): Ticket {
  const [row] = enrichStreamRowsWithUserMap(
    enrichStreamRowsWithPriorityMap([rowFromUnknown(raw)], priorityIdToLabel),
    userDisplayMap,
    agents
  );
  const base = streamRowToTicket(row);
  const stubs = buildAttachmentsFromTicketRecord(raw);
  const priorityFields = ticketPriorityFieldsFromApi(
    {
      priorityId: row.priorityId,
      priority: row.priority,
      priorityLabelFromApi:
        typeof raw.priority_label === "string"
          ? raw.priority_label
          : undefined,
    },
    priorityIdToLabel,
    slaPriorityById
  );

  return {
    ...base,
    ...priorityFields,
    assignee: row.assignedAgent ?? undefined,
    isVip: parseIsVipFromApi(raw.is_vip, raw.isVip, base.isVip),
    isSla: parseIsSlaFromApi(raw.is_sla, raw.isSla, base.isSla),
    isExit: parseIsExitFromApi(raw.is_exit, raw.isExit),
    isPts: parseIsPtsFromApi(raw.is_pts, raw.isPts),
    projectId: readIdLike(raw.project_id ?? raw.projectId ?? raw.project),
    projectName: pickProjectNameFromApi(raw) ?? base.projectName,
    changeRequest: parseChangeRequestSummary(raw) ?? undefined,
    attachments: stubs,
    attachmentFileIds: stubs.map(s => s.id),
  };
}
